
import React, { useRef, useEffect } from 'react';
import { Message } from './ChatInterface';
import ChatMessage from './ChatMessage';
import { AIAgent } from '@/hooks/useCareerChat';
import { ScrollArea } from '@/components/ui/scroll-area';

interface MessageListProps {
  messages: Message[];
  isTyping?: boolean;
  speakText?: (text: string) => void;
  isAuthenticated?: boolean;
  agent?: AIAgent | null;
}

const MessageList: React.FC<MessageListProps> = ({ 
  messages, 
  isTyping = false, 
  speakText,
  isAuthenticated = false,
  agent
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Auto-scroll to bottom on new messages - only within the scroll area
  useEffect(() => {
    if (messagesEndRef.current) {
      // Scroll the element into view within its container, not the window
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest'
      });
    }
  }, [messages, isTyping]);
  
  const handleSpeakClick = (text: string) => {
    if (speakText && isAuthenticated) {
      speakText(text);
    }
  };
  
  return (
    <ScrollArea className="flex-1 p-4 pb-0">
      <div className="space-y-4">
        {messages.map((message) => (
          <ChatMessage
            key={message.id}
            content={message.content}
            isUser={message.isUser}
            timestamp={message.timestamp}
            onSpeakClick={isAuthenticated ? handleSpeakClick : undefined}
            agent={agent}
          />
        ))}
        
        {isTyping && (
          <div className="flex items-center space-x-2 text-muted-foreground">
            <div className="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <div className="text-sm">Typing...</div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
    </ScrollArea>
  );
};

export default MessageList;
